<template>
  <div
    class="truncate pl-1 pb-1"
    style="width: 150px">
    <span class="font-bold text-gray-600">
      問題選択数 {{ selectedQuestions.length }}/{{ pagination.data.length }}
    </span>
  </div>
  <div class="shadow-lg bg-white mr-1 rounded-b-lg rounded-t">
    <TabGroup>
      <TabList class="flex p-1 space-x-1 bg-blue-900/20 rounded-xl">
        <Tab
          v-for="(name, key) in examinationTypes"
          :key="key"
          as="template">
          <button
            :class="[
              'w-full py-2.5 text-sm text-white leading-5 font-medium rounded-lg',
              'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-blue-400 ring-white ring-opacity-60',
              key == defaultIndex + 1
                ? 'bg-blue-600 shadow'
                : 'text-black hover:bg-white/[0.12] hover:text-white',
            ]"
            @click="tabClicked(key)">
            {{ name }}
          </button>
        </Tab>
      </TabList>
      <TabPanels class="mt-2">
        <TabPanel>
          <div v-if="pagination.data.length">
            <data-table
              :headers="headers"
              :items="pagination.data"
              @sort-item="onSortedQuestion"
              @row-click="onRowClicked">
              <template #th-checkbox>
                <input
                  v-model="selectedCurrentPageQuestions"
                  type="checkbox"
                  class="bg-gray-50 border-gray-300 focus:ring-3 focus:ring-blue-300 h-4 w-4 rounded focus:outline-none" />
              </template>
              <template #check_box="{ item }">
                <input
                  v-model="selectedQuestions"
                  :value="item.id"
                  type="checkbox"
                  class="bg-gray-50 border-gray-300 focus:ring-3 focus:ring-blue-300 h-4 w-4 rounded focus:outline-none"
                  @click="onClickedCheckBox" />
              </template>
              <template #subject_id="{ item }">
                {{ subjectName(item.subject_id) }}
              </template>
              <template #field_name="{ item }">
                {{ item.field?.title ?? item.field_name }}
              </template>
              <template #question="{ item }">
                <div
                  class="truncate"
                  style="width: 220px">
                  {{ item.question }}
                </div>
              </template>
              <template #correct_answer="{ item }">
                <div
                  class="truncate"
                  style="width: 200px">
                  {{ item.correct_answer }}
                </div>
              </template>
            </data-table>
            <div class="mb-5 pb-5">
              <div class="mt-4">
                <pagination
                  :paginate="pagination"
                  @page-previous="onPaginationChanged"
                  @page-next="onPaginationChanged"
                  @page-click="onPaginationChanged" />
              </div>
            </div>
          </div>
          <div
            v-else
            class="py-4">
            <div
              class="mx-auto w-11/12 bg-red-100 border border-red-400 text-red-500 px-4 py-3 rounded relative text-center"
              role="alert">
              <span class="block sm:inline font-bold">問題が見つかりませんでした。</span>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </TabGroup>
  </div>
</template>

<script setup>
import { Inertia } from '@inertiajs/inertia'
import {
  ref, reactive, computed, watch,
} from 'vue'
import {
  TabGroup, TabList, Tab, TabPanels, TabPanel,
} from '@headlessui/vue'
import { usePage } from '@inertiajs/inertia-vue3'
import DataTable from '@/Common/DataTable'
import Pagination from '../../../Common/Pagination'

const props = defineProps({
  pagination: {
    type: Object,
    required: true,
  },
  examination: {
    type: Object,
    required: false,
  },
  subject: {
    type: Object,
    required: false,
  },
  subjectNames: {
    type: Array,
    default: () => [],
  },
  currentExaminationType: {
    type: Number,
    default: 1,
  },
  limit: {
    type: String,
    default: '10',
  },
  isExpired: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['select-questions', 'change-tab', 'initialize-questions'])

const subjectName = (subjectId) => props.subjectNames.find((item) => item.id === subjectId).name

const headers = {
  check_box: '',
  id: 'ID',
  subject_id: '科目名',
  field_name: '分野',
  question: '問題文',
  correct_answer: '正答',
}

const examinationTypes = computed(() => usePage().props.value.examinationTypes)

const defaultIndex = computed(() => props.currentExaminationType - 1)
const tabClicked = (value) => emit('change-tab', value)

const selectedQuestions = ref([])
const selectedCurrentPageQuestions = computed({
  get: () => false,
  set: (bool) => {
    if (bool) {
      selectedQuestions.value = props.pagination.data.map((item) => item.id)
    } else {
      selectedQuestions.value = []
    }
  },
})

const targetQuestion = (id) => props.pagination.data.find((item) => item.id === id)
const selectedQuestionObjects = computed(() => selectedQuestions.value.map((id) => targetQuestion(id)))

const initializedSelectedQuestions = () => {
  selectedQuestions.value.length = 0
}

watch(
  () => selectedQuestionObjects,
  (newVal) => {
    emit('select-questions', newVal)
    emit('initialize-questions', initializedSelectedQuestions)
  },
  { deep: true },
)

const onClickedCheckBox = (e) => e.stopPropagation()

const onSortedQuestion = (...indexes) => {
  if (props.isExpired === false) {
    Inertia.get(route('examinations.sortQuestion', {
      examination: props.examination,
      indexes,
      examinationType: props.currentExaminationType,
      limit: props.limit,
      page: props.pagination.current_page,
    }))
  }
}

const onRowClicked = (item, title) => {
  if (title) {
    Inertia.get(route('examinations.show', {
      examination: props.examination.id,
      examinationId: props.examination.id,
      examinationType: props.currentExaminationType,
      targetId: item.id,
      question: item,
    }))
  }
}

const onPaginationChanged  = (page) => {
  Inertia.get(route('examinations', {
    examination: props.examination,
    page,
    limit: props.limit,
    examinationType: props.currentExaminationType,
  }))
}
</script>
